import classNames from 'classnames'
import { useState, useRef, useEffect } from 'react'
import gsapInstance from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './Video.module.scss'
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useOnload } from '../../hooks/useOnload';

gsapInstance.registerPlugin(ScrollTrigger);

export const Video = (props) => {
  const [isVideoPlaying, setVideoPlaying] = useState(false)
  const videoParentRef = useRef(null)
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const breakpoint = useBreakpoint();
  const onload = useOnload();

  const handleVideoPlay = () => {
    if(props.launch === 'youtube') {
      window.open(props.src, '_blank');

      return
    }

    videoRef.current.play()
    setVideoPlaying(true)
  }

  const handleVideoPause = () => {
    videoRef.current.pause()
    if(!document.fullscreenElement) setVideoPlaying(false)
  }

  const handleVideoEnded = () => {
    if(!document.fullscreenElement) setVideoPlaying(false)
  }

  useEffect(() => {
    if(props.launch === 'youtube') return;

    videoRef.current.volume = .5
  }, [props.launch, videoRef])

  useGSAP(() => {
    if(props.launch === 'youtube') return;

    if(onload && videoParentRef.current) {
      gsapInstance.set(videoParentRef.current, { scale: 0 })
      gsapInstance.to(videoParentRef.current, 
        {
          scale: 1,
          duration: 2,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top center+=25%',
            once: true,
          }
        }
      )
    }
  }, [onload, breakpoint], containerRef)

  return (
    <div className={ styles['container'] } ref={ containerRef }>
      <div
        className={classNames(
          styles['video'],
          {[styles['video--playing']]: isVideoPlaying}
        )}
        ref={videoParentRef}
        onMouseEnter={props.launch === 'hover' ? handleVideoPlay : null}
        onMouseLeave={props.launch === 'hover' ? handleVideoPause : null}
      >
        <div
          className={classNames(
            styles['video__content'],
            {[styles[`video__content--${props.launch}`]]: props.launch}
          )}
          style={{
            backgroundImage: `url(${props.poster})`
          }}
        >
          { props.title && (
            <p className={classNames(styles['video__content-title'])}>{props.title}</p>
          ) }
          { props.more && (
            <div className={classNames(styles['video__content-more'])}>
              <a
                className={classNames(styles['video__btn'])}
                href={props.more.href}
                target={props.more.target || '_blank'}
                rel="noreferrer"
              >
                <span className={classNames(styles['video__btn-text'])}>{props.more.text}</span>
                <span
                  className={classNames(
                    styles['video__btn-icon'],
                    styles['video__btn-icon--more']
                  )}
                />
              </a>
            </div>
          ) }
          { (props.launch === 'click' || props.launch === 'youtube') && (
            <div className={classNames(styles['video__content-play'])} onClick={handleVideoPlay} />
          ) }
        </div>
        { props.launch !== 'youtube' && (
          <video
            ref={videoRef}
            className={classNames(styles['video__video'])}
            src={props.src}
            preload="metadata"
            onPlay={props.launch === 'click' ? handleVideoPlay : null}
            onPause={props.launch === 'click' ? handleVideoPause : null}
            onEnded={props.launch === 'click' ? handleVideoEnded : null}
            controls={props.launch === 'click'}
            loop={props.launch === 'hover'}
            playsInline
            muted={props.isMuted ? props.isMuted : true}
          />
        ) }
      </div>
    </div>
  )
}
